import { Box, styled, Breadcrumbs } from '@mui/material';
import { ReportTable } from './ReportTable';
import { ReportInfo } from './ReportInfo';
import { ReportTypeIcon } from './ReportTypeIcon';
import { BodyText } from '@/components/BodyText';
import { Ellipsis } from '@/components/Ellipsis/index';
import { useParams, Link } from 'react-router-dom';
import { useGetReport } from '@/hooks/api/reports/useGetReport';
import { useInfiniteReportGroups } from '@/hooks/api/reports/useInfiniteReportGroups';
import { REPORT_TITLES } from './consts';
import { ReportPageObjectModel } from '@/poms/reportPageObjectModel';
import { TableChart as ReportsIcon } from '@mui/icons-material';
import { useTenants } from '@/services/tenants/useTenants';
import { DndContext, DragOverlay } from '@dnd-kit/core';
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { useReportDnd } from './useReportDnd';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { ReportDragProvider } from './ReportDragContext';

const HeaderRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '12px 12px 6px 12px',
  minWidth: 0,
});

const ContentBox = styled(Box)({
  flex: 1,
  display: 'flex',
  justifyContent: 'flex-start',
  overflow: 'hidden',
  margin: '0 0 6px 0',
  height: '100%',
  minHeight: 0,
  minWidth: 0,
  gap: '6px',
  padding: '6px',
});

const LeftColumn = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  minHeight: 0,
  width: '270px',
  flexShrink: 0,
});

const ReportHeader = () => {
  const { id, groupId } = useParams();
  const { report, isLoading: isLoadingReport } = useGetReport({ id });
  const { groups, isLoading: isLoadingGroups } = useInfiniteReportGroups({ id });
  const { getPath } = useTenants();

  const selectedGroup = groups.find(group => group.id === groupId);

  if (isLoadingReport || isLoadingGroups || !report) {
    return;
  }

  const groupName = selectedGroup?.name || '';

  return (
    <HeaderRow>
      <Breadcrumbs
        data-testid={ReportPageObjectModel.ReportBreadcrumbs}
        sx={{
          color: 'text.secondary',
          flex: 1,
          minWidth: 0,
          '& .MuiBreadcrumbs-ol': {
            flexWrap: 'nowrap',
            alignItems: 'center',
          },
          '& .MuiBreadcrumbs-li': {
            overflow: 'hidden',
            minWidth: 0,
            display: 'flex',
            alignItems: 'center',
          },
          '& .MuiBreadcrumbs-li:first-of-type': {
            flexShrink: 0,
          },
        }}
      >
        <Link
          to={getPath('report')}
          style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '6px' }}
        >
          <ReportsIcon sx={{ fontSize: '24px' }} />
          <BodyText>Reports</BodyText>
        </Link>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px', minWidth: 0 }}>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
            <ReportTypeIcon reportType={report.reportType} />
          </Box>
          <BodyText sx={{ minWidth: 0 }}>
            <Ellipsis text={report.name || REPORT_TITLES[report.reportType]} />
          </BodyText>
        </Box>
        {groupName && (
          <BodyText sx={{ minWidth: 0 }}>
            <Ellipsis text={groupName} />
          </BodyText>
        )}
      </Breadcrumbs>
    </HeaderRow>
  );
};

export const ReportContent = () => {
  const { id, groupId } = useParams();
  const gridApiReference = useGridApiRef();

  return (
    <ReportDragProvider groupId={groupId} gridApiReference={gridApiReference}>
      <ReportContentInner reportId={id} />
    </ReportDragProvider>
  );
};

const ReportContentInner = ({ reportId }: { reportId: string | undefined }) => {
  const { sensors, handleDragStart, handleDragEnd, dragOverlay, isDragging, dropAnimation } = useReportDnd(reportId);

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      modifiers={[snapCenterToCursor]}
    >
      <Box
        data-testid={ReportPageObjectModel.ReportContent}
        sx={{ height: '100%', display: 'flex', flexDirection: 'column', minWidth: 0, overflow: 'hidden' }}
      >
        <ReportHeader />
        <ContentBox>
          <LeftColumn>
            <ReportInfo isDragging={isDragging} />
          </LeftColumn>
          <ReportTable />
        </ContentBox>
      </Box>
      <DragOverlay dropAnimation={dropAnimation}>{dragOverlay}</DragOverlay>
    </DndContext>
  );
};
